import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { GameButton } from '../../game-button/game-button';
import clsx from 'clsx';

import { Avatar } from 'shared/components/ui/Avatar/Avatar';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { coinIcons } from 'shared/constants';
import { RoomStatusEnum } from 'shared/constants/shared-enums';
import { useAccount } from 'shared/hooks/use-web3';
import { useGlobalStore } from 'shared/store/globalStore';
import { genericFormatBet } from 'shared/utils/betFormater';
import { prettyTx } from 'shared/utils/pretty-tx';
import { CurrencyType } from 'shared/web3';

import Heart from 'assets/png/heart-filled.png?format=webp;avif&as=picture';

import useLiveGameItem from './use-live-game-item';

interface ILiveGameItemProps {
  roomId: bigint;
  address: `0x${string}`;
  currency?: CurrencyType;
  amount: number;
  ourGame: boolean;
  joinRoomHandler?: (roomId: number, bet?: number) => void;
  status: RoomStatusEnum;
  wins?: number;
}

export const LiveGameItem = ({
  roomId,
  address,
  currency,
  amount,
  ourGame,
  joinRoomHandler,
  status,
  wins,
}: ILiveGameItemProps) => {
  const { address: userAddress, chainId } = useAccount();
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const leaderboardMap = useGlobalStore((state) => state.leaderboardMap);
  const {
    isItemHovered,
    isError,
    errorMessage,
    loading,
    handleShowButton,
    handleHideButton,
    handleJoinGame,
    normalizedAmount,
  } = useLiveGameItem({
    currency,
    userAddress,
    roomId: Number(roomId),
    amount,
    containerRef,
    joinRoomHandler,
  });
  const screenAddress = useMemo(
    () =>
      prettyTx({
        address,
        leaderboardMap,
        startAddressLength: 2,
        endAddressLength: 4,
        discordUsernameLength: 9,
      }),
    [address, leaderboardMap],
  );
  return (
    <div
      ref={containerRef}
      className="w-full"
      onMouseOver={handleShowButton}
      onMouseOut={handleHideButton}
      data-testid="live-game-item"
    >
      <div
        className={`grid grid-cols-[2fr_1fr_1fr_1.5fr] rounded-md border border-[#FFFFFF40] bg-gradient-to-r from-[#05055B] to-[#151552] p-1.5 transition-all sm:rounded-2xl sm:border-2`}
      >
        <div className="items flex items-center gap-[12px]">
          <div className="h-5 w-5 rounded-[4px] bg-[linear-gradient(180deg,#000BFF_0%,#FFC000_100%)] p-0.5 sm:h-[60px] sm:w-[60px] sm:rounded-xl">
            <Avatar
              userAddress={`0x${address}`}
              className="rounded-[4px] bg-[#112A66] sm:rounded-xl"
            />
          </div>
          <div className="flex flex-col">
            <span
              className={clsx(
                'min-w-[9.75rem] font-chivo text-[9px] font-medium text-yellow sm:text-[20px]',
                {
                  'text-[#60E9A2]':
                    ourGame &&
                    status === RoomStatusEnum.Open &&
                    userAddress?.toLowerCase() !== address?.toLowerCase(),
                  'text-white':
                    ourGame &&
                    status === RoomStatusEnum.Open &&
                    userAddress?.toLowerCase() === address?.toLowerCase(),
                },
              )}
            >
              {screenAddress}
            </span>
            <span className="text-[9px] font-medium text-white opacity-75 sm:text-[14px]">
              {t('pages.liveGamesSection.id')} {roomId.toString()}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2">
          {currency?.symbol ? (
            <img
              src={coinIcons[currency.symbol as keyof typeof coinIcons]}
              alt="coin"
              className="h-9 w-9"
            />
          ) : null}
          <div>
            <p className="text-[20px] font-semibold text-white opacity-75">
              {genericFormatBet(normalizedAmount.toString(), 'text-[20px] mb-2 px-0.5')}
            </p>

            <p className="text-base font-semibold text-white opacity-50">{currency?.symbol}</p>
          </div>
        </div>

        <div className="relative flex items-center justify-center gap-2">
          <OptimizedImage imageData={Heart} alt="heart" className="h-7 w-7" />
          <OptimizedImage
            imageData={Heart}
            alt="heart"
            className={`h-7 w-7 ${wins > 3 ? 'opacity-50' : ''}`}
          />

          {wins !== 3 && wins > 0 && (
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[36px] font-semibold leading-none text-white">
              {wins}
            </span>
          )}
          <OptimizedImage imageData={Heart} alt="heart" className="h-7 w-7" />
        </div>
        <div className="relative flex items-center justify-end">
          <GameButton
            address={address}
            userAddress={userAddress}
            status={status}
            ourGame={ourGame}
            chainId={chainId}
            roomId={roomId}
            loading={loading}
            isError={isError}
            errorMessage={errorMessage}
            isItemHovered={isItemHovered}
            handleJoinGame={handleJoinGame}
          />
        </div>
      </div>
    </div>
  );
};
